import React from 'react'
import { Button } from '@/components/ui/button'
import { Heading3 } from '@/components/ui/typography'
import { DemoAccountDetails } from 'api/Clients/Types/DemoAccountDetails'
import { WelcomeAccountDetails } from 'api/Clients/Types/WelcomeAccountDetails'
import {
  FormBaseErrorMessage,
  FormBaseFieldBlock,
  FormBaseFieldGroup,
  FormBaseFieldLabel,
  FormBaseRow,
  FormRowReversed,
} from 'components/Form/Form.styles'
import { Field, Form, FormikErrors, FormikProps, withFormik } from 'formik'
import { MultiAdjustmentTemplate } from 'routes/Transactions/Transactions.styles'
import { FormSubmitValues, TwoDecimalPlacesPattern } from 'utils/formValidation'

import { AccountFundsType, isAddFunds } from './DemoWelcomeAccountDetailsForm'

export interface FundsTradingDemoWelcomeAccountFormValues {
  tradingAccountId: string
  amount: string
  comment?: string
}

const FundsTradingDemoWelcomeAccountFormUI: React.FC<
  FormikProps<FundsTradingDemoWelcomeAccountFormValues> & OuterProps
> = (props) => {
  const { handleSubmit, errors, isSubmitting, setSubmitting } = props

  const submit = () => {
    setSubmitting(true)
    handleSubmit()
  }

  return (
    <MultiAdjustmentTemplate>
      <Form>
        <Heading3>
          {isAddFunds(props.fundType) ? 'Add funds to' : 'Deduct funds from'} {props.data.name}{' '}
          <span className='blue'>({props.data.accountGroup.accountGroupType.name})</span>
        </Heading3>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Amount</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field
                name='amount'
                type='number'
                min={0}
                step={0.01}
                pattern='^\d+(?:\.\d{1,2})?$'
              />
            </FormBaseFieldGroup>
          </FormBaseRow>
          {errors.amount && <FormBaseErrorMessage>{errors.amount}</FormBaseErrorMessage>}
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormBaseFieldLabel>Comment</FormBaseFieldLabel>
            <FormBaseFieldGroup>
              <Field
                component='textarea'
                name='comment'
                placeholder='Enter comment'
                rows='3'
                maxLength='130'
              />
            </FormBaseFieldGroup>
          </FormBaseRow>
        </FormBaseFieldBlock>
        <FormBaseFieldBlock>
          <FormBaseRow>
            <FormRowReversed>
              <Button disabled={isSubmitting} type='button' onClick={submit}>
                Submit
              </Button>
            </FormRowReversed>
          </FormBaseRow>
        </FormBaseFieldBlock>
      </Form>
    </MultiAdjustmentTemplate>
  )
}

interface OuterProps {
  fundType: AccountFundsType
  data: DemoAccountDetails | WelcomeAccountDetails
  onSubmit(values: FormSubmitValues<FundsTradingDemoWelcomeAccountFormValues>): Promise<void>
}

export const FundsTradingDemoWelcomeAccountForm = withFormik<
  OuterProps,
  FundsTradingDemoWelcomeAccountFormValues
>({
  mapPropsToValues: (props) => {
    const { data } = props
    return {
      tradingAccountId: data.id,
      comment: '',
      amount: '0',
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    await props.onSubmit(values)
    setSubmitting(false)
  },
  validate: (values) => {
    const errors: FormikErrors<FundsTradingDemoWelcomeAccountFormValues> = {}
    if (!values.amount) {
      errors.amount = 'required'
    }
    if (!!values.amount && !TwoDecimalPlacesPattern.test(values.amount.toString())) {
      errors.amount = 'Invalid amount. Allowed are only positive and 2 decimal places amounts.'
    }
    return errors
  },
  enableReinitialize: true,
  isInitialValid: false,
})(FundsTradingDemoWelcomeAccountFormUI)
